import { Koi } from '@ksyos/koi';
import type { ISchemaContainer } from './schemaContainer';
import { DiagnosticReportValidationTypes } from '../enums/diagnosticReportValidationTypes';
import { SupervisionValidationTypes } from '../enums/supervisionValidationTypes';
import { SupervisorsAssessmentBiometrics } from '../enums/supervisorsAssessmentBiometrics';
import {
    tfoV1SupervisionCustomControlData,
    tfoV1SupervisionCustomControlDataDraft,
} from './customControls/ophthalmology';
import { pilotExaminationV1FinalSupervision } from './customControls/pilot';
import { getDiagnosticReportSchemas } from './diagnosticReport';

export interface ISupervisionValidated {
    assessmentBiometrics: SupervisorsAssessmentBiometrics;
    customControlData: any;
    id?: string;
    remarkBiometrics: string | null;
    remarkGrading: string | null;
}

export interface ISaveAndSaveSendValidated {
    supervision: ISupervisionValidated;
    validationType: SupervisionValidationTypes;
}

export interface ISupervisionEditPerformerValidated {
    performerOrganizationId: string;
    performerPractitionerId: string;
}

const supervisionEditPerformer = Koi.object().keys({
    performerOrganizationId: Koi.string().uuid(),
    performerPractitionerId: Koi.string().uuid(),
});

export const supervisionEditPerformerSchema: ISchemaContainer<ISupervisionEditPerformerValidated> = {
    schema: supervisionEditPerformer.options({ convert: false, presence: 'required' }),
};

const supervisionBase = Koi.object().keys({
    id: Koi.string().uuid().optional(),
    remarkBiometrics: Koi.string().max(10000).allow(null),
    remarkGrading: Koi.string().max(10000).allow(null),
});

export function getSaveAndSendValidationSchema(
    supervisionValidationType: SupervisionValidationTypes,
    isDraft?: boolean
): ISchemaContainer<ISaveAndSaveSendValidated> {
    return {
        schema: Koi.object({
            supervision: getSupervisionSchema(supervisionValidationType, isDraft),
            validationType: Koi.koi().enum(SupervisionValidationTypes).disallow(null),
        }).options({ convert: false, presence: 'required' }),
    };
}

export function getSupervisionValidationSchema(
    supervisionValidationType: SupervisionValidationTypes,
    isDraft?: boolean
): ISchemaContainer<ISupervisionValidated> {
    return {
        schema: getSupervisionSchema(supervisionValidationType, isDraft).options({
            convert: false,
            presence: 'required',
        }),
    };
}

function getSupervisionSchema(supervisionValidationType: SupervisionValidationTypes, isDraft?: boolean) {
    if (isDraft) {
        return supervisionBase.concat(
            Koi.object({
                assessmentBiometrics: Koi.koi().enum(SupervisorsAssessmentBiometrics).allow(null),
                customControlData: getSchemas(supervisionValidationType).disallow(null),
            })
        );
    }

    return supervisionBase.concat(
        Koi.object({
            assessmentBiometrics: Koi.koi().enum(SupervisorsAssessmentBiometrics),
            customControlData: getSchemas(supervisionValidationType).disallow(null),
        })
    );
}

function getSchemas(supervisionValidationType: SupervisionValidationTypes) {
    switch (supervisionValidationType) {
        case SupervisionValidationTypes.tpoV1EditSupervision:
            return getDiagnosticReportSchemas(DiagnosticReportValidationTypes.tpoV1EditFinalDiagnosticReport)
                .customControlSchema;
        case SupervisionValidationTypes.tcorV1EditSupervision:
            return getDiagnosticReportSchemas(DiagnosticReportValidationTypes.tcorV1EditFinalDiagnosticReport)
                .customControlSchema;
        case SupervisionValidationTypes.tcohV1EditSupervision:
            return getDiagnosticReportSchemas(DiagnosticReportValidationTypes.tcohV1EditFinalDiagnosticReport)
                .customControlSchema;
        case SupervisionValidationTypes.tcohV2EditSupervision:
            return getDiagnosticReportSchemas(DiagnosticReportValidationTypes.tcohV2EditFinalDiagnosticReport)
                .customControlSchema;
        case SupervisionValidationTypes.tiecgV1EditSupervision:
            return getDiagnosticReportSchemas(DiagnosticReportValidationTypes.tiecgV1EditFinalDiagnosticReport)
                .customControlSchema;
        case SupervisionValidationTypes.tfoV1EditSupervision:
            return tfoV1SupervisionCustomControlData;
        case SupervisionValidationTypes.pilotExaminationV1EditSupervision:
            return pilotExaminationV1FinalSupervision;
        case SupervisionValidationTypes.tpoV1EditSupervisionDraft:
            return getDiagnosticReportSchemas(DiagnosticReportValidationTypes.tpoV1EditDraftDiagnosticReport)
                .customControlSchema;
        case SupervisionValidationTypes.tcorV1EditSupervisionDraft:
            return getDiagnosticReportSchemas(DiagnosticReportValidationTypes.tcorV1EditDraftDiagnosticReport)
                .customControlSchema;
        case SupervisionValidationTypes.tcohV1EditSupervisionDraft:
            return getDiagnosticReportSchemas(DiagnosticReportValidationTypes.tcohV1EditDraftDiagnosticReport)
                .customControlSchema;
        case SupervisionValidationTypes.tcohV2EditSupervisionDraft:
            return getDiagnosticReportSchemas(DiagnosticReportValidationTypes.tcohV2EditDraftDiagnosticReport)
                .customControlSchema;
        case SupervisionValidationTypes.tiecgV1EditSupervisionDraft:
            return getDiagnosticReportSchemas(DiagnosticReportValidationTypes.tiecgV1EditDraftDiagnosticReport)
                .customControlSchema;
        case SupervisionValidationTypes.tfoV1EditSupervisionDraft:
            return tfoV1SupervisionCustomControlDataDraft;
        case SupervisionValidationTypes.pilotExaminationV1EditSupervisionDraft:
            return pilotExaminationV1FinalSupervision;
        default:
            throw new Error('Unable to find supervision validation schema');
    }
}
